import React, { useState, useEffect, useRef, useMemo, useId } from 'react';
import ReactPlayer from 'react-player';
import { AlertCircle, Volume2, VolumeX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMediaSettings } from '@/providers/MediaSettingsProvider';

// Constants to prevent object recreation
const INTERSECTION_THRESHOLDS = [0, 0.5, 1];

interface MediaEmbedProps {
  url: string;
  title?: string;
  className?: string;
  onEnded?: () => void;
}

const MediaEmbed: React.FC<MediaEmbedProps> = ({ url, title = '', className, onEnded }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [showTapToUnmuteOverlay, setShowTapToUnmuteOverlay] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const videoId = useId();
  const { soundEnabled, toggleSound, currentPlayingVideo, setCurrentPlayingVideo } = useMediaSettings();
  const prevVisibleRef = useRef(false);
  const programmaticPauseRef = useRef(false);

  // Intersection observer for autoplay detection
  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    const setupObserver = () => {
      if (!containerRef.current || observer) return;

      observer = new IntersectionObserver(
        ([entry]) => {
          setIsVisible(entry.intersectionRatio > 0.5);
        },
        {
          threshold: INTERSECTION_THRESHOLDS,
          rootMargin: '10px 0px 10px 0px'
        }
      );

      observer.observe(containerRef.current);
    };

    timeoutId = setTimeout(setupObserver, 0);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };
  }, []);

  // Determine if this video should be playing (respect userPaused only)
  const shouldPlay = isVisible && currentPlayingVideo === videoId && !userPaused;

  // Media type detection
  const { mediaType, canPlay } = useMemo(() => {
    const isVideo = ReactPlayer.canPlay(url);
    const isImage = url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);

    return {
      mediaType: isImage ? 'image' : isVideo ? 'video' : 'unknown',
      canPlay: isVideo
    };
  }, [url]);

  // Claim playback whenever this playable video is visible and not user-paused.
  // This is more robust than edge-triggering on visibility transitions only.
  useEffect(() => {
    if (canPlay && isVisible && !userPaused && currentPlayingVideo !== videoId) {
      setCurrentPlayingVideo(videoId);
    }
    prevVisibleRef.current = isVisible;
  }, [canPlay, isVisible, userPaused, currentPlayingVideo, videoId, setCurrentPlayingVideo]);

  // Handle play events
  const handlePlay = () => {
    setUserPaused(false);
    setCurrentPlayingVideo(videoId);
    setIsPlaying(true);
  };

  // Handle pause events: mark userPaused only if NOT programmatic
  const handlePause = () => {
    setIsPlaying(false);
    if (programmaticPauseRef.current) {
      programmaticPauseRef.current = false;
      return;
    }
    setUserPaused(true);
  };

  // Auto-pause when visibility changes or another video plays (do not mark userPaused)
  useEffect(() => {
    if (playerRef.current && isPlaying && !shouldPlay) {
      try {
        programmaticPauseRef.current = true;
        playerRef.current.pause();
      } catch {}
    }
  }, [shouldPlay, isPlaying]);

  // Show a temporary "Tap to Unmute" overlay when media is muted and visible.
  useEffect(() => {
    if (mediaType !== 'video' || !canPlay || !isVisible || soundEnabled) {
      setShowTapToUnmuteOverlay(false);
      return;
    }

    setShowTapToUnmuteOverlay(true);
    const timeoutId = window.setTimeout(() => {
      setShowTapToUnmuteOverlay(false);
    }, 5000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [mediaType, canPlay, isVisible, soundEnabled, url]);

  // Error fallback
  if (error || mediaType === 'unknown') {
    return (
      <div className={cn("mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2", className)}>
        <AlertCircle className="h-4 w-4 text-destructive" />
        <div className="text-sm text-destructive">
          Unable to load media.{" "}
          <a href={url} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
            View original link
          </a>
        </div>
      </div>
    );
  }

  // Image handling
  if (mediaType === 'image') {
    return (
      <div ref={containerRef} className={cn("mt-4", className)}>
        <div className="relative w-full rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
          <img
            src={url}
            alt={title || 'Image'}
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setError(true)}
          />
        </div>
      </div>
    );
  }

  // Video handling with ReactPlayer
  return (
    <div ref={containerRef} className={cn("mt-4", className)}>
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16/9' }}>
        {/* Sound toggle */}
        <button
          type="button"
          aria-label={soundEnabled ? 'Mute all media' : 'Unmute all media'}
          className="absolute top-2 right-2 z-10 rounded-full bg-black/60 text-white p-2 hover:bg-black/70 transition"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleSound();
          }}
        >
          {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </button>

        {showTapToUnmuteOverlay && (
          <button
            type="button"
            aria-label="Tap to unmute"
            className="absolute left-1/2 top-1/4 z-10 -translate-x-1/2 rounded-full bg-black/90 px-12 py-5 text-2xl font-semibold text-white shadow-sm backdrop-blur-[1px] transition hover:bg-black/95"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleSound();
            }}
          >
            Tap to Unmute
          </button>
        )}

        {/* ReactPlayer */}
        <ReactPlayer
          ref={playerRef}
          src={url}
          playing={shouldPlay}
          muted={!soundEnabled}
          volume={soundEnabled ? 1 : 0}
          controls
          width="100%"
          height="100%"
          pip={false}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={onEnded}
          onError={() => setError(true)}
          config={{
            youtube: {
              rel: 0,
              enablejsapi: 1,
              origin: window.location.origin
            }
          }}
        />
      </div>
    </div>
  );
};

export default MediaEmbed;